/**
 * Billing Hub Navigation Configuration
 * 
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  LayoutDashboard,
  DollarSign,
  Receipt,
  CheckCircle,
  Wand2,
  Users,
  BarChart,
  Settings,
  Briefcase,
  FileEdit,
  Plus,
  ShoppingCart,
  FileText,
  Package,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'billing' as HubId,
  name: 'Billing Hub',
  icon: DollarSign,
  accentColor: 'emerald',
  accentGradient: 'from-emerald-500 to-green-600',
  baseUrl: 'https://billing.quotech.ai',
}

// =============================================================================
// MAIN NAVIGATION (Core Work)
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/dashboard',
  },
  {
    id: 'book_keeping',
    label: 'Bills',
    icon: Receipt,
    href: '/book_keeping',
  },
  {
    id: 'payment_schedule',
    label: 'Cash-out Schedule',
    icon: TrendingUp,
    href: '/payment_schedule',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: TrendingUp,
    href: '/analytics',
  },
  {
    id: 'purchase_order',
    label: 'Purchase Orders',
    icon: ShoppingCart,
    href: '/purchase_order',
  },
  {
    id: 'quotations',
    label: 'Quotations',
    icon: FileText,
    href: '/quotations',
  },
  {
    id: 'pricelist',
    label: 'Price List',
    icon: Package,
    href: '/pricelist',
  },
  {
    id: 'helper',
    label: 'AI Helper',
    icon: Sparkles,
    href: '/helper',
  },
]

// =============================================================================
// SECONDARY NAVIGATION (Tools)
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'extract',
    label: 'AI Extract',
    icon: Wand2,
    href: '/extract',
  },
  {
    id: 'suppliers',
    label: 'Supplier Matching',
    icon: Users,
    href: '/suppliers',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: BarChart,
    href: '/reports',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('billing')

// =============================================================================
// QUICK ADD ITEMS (Header dropdown)
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'upload-bill',
    label: 'Upload Bill',
    icon: Plus,
    action: () => { window.location.href = '/book_keeping?upload=1' },
    shortcut: '⌘⇧B',
  },
  {
    id: 'new-po',
    label: 'New Purchase Order',
    icon: ShoppingCart,
    action: () => { window.location.href = '/purchase_order?new=1' },
  },
  {
    id: 'request-quote',
    label: 'Request Quote',
    icon: FileText,
    action: () => { window.location.href = '/quotations?new=1' },
  },
]

// =============================================================================
// COMMAND PALETTE COMMANDS
// =============================================================================

export const COMMANDS: CommandItem[] = [
  {
    id: 'upload-bill',
    label: 'Upload Bill',
    description: 'Upload an invoice/bill for AI processing',
    icon: Plus,
    action: () => { window.location.href = '/book_keeping?upload=1' },
    keywords: ['upload', 'add', 'bill', 'invoice'],
    category: 'action',
  },
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    icon: LayoutDashboard,
    action: () => { window.location.href = '/dashboard' },
    keywords: ['navigate', 'home', 'dashboard'],
    category: 'navigation',
  },
  {
    id: 'go-bills',
    label: 'Go to Bills (Bookkeeping)',
    icon: Receipt,
    action: () => { window.location.href = '/book_keeping' },
    keywords: ['navigate', 'bills', 'invoices', 'bookkeeping'],
    category: 'navigation',
  },
  {
    id: 'go-analytics',
    label: 'Go to Analytics',
    icon: TrendingUp,
    action: () => { window.location.href = '/analytics' },
    keywords: ['navigate', 'analytics', 'reports', 'spend'],
    category: 'navigation',
  },
  {
    id: 'go-pos',
    label: 'Go to Purchase Orders',
    icon: ShoppingCart,
    action: () => { window.location.href = '/purchase_order' },
    keywords: ['navigate', 'purchase', 'po', 'orders'],
    category: 'navigation',
  },
  {
    id: 'go-quotes',
    label: 'Go to Quotations',
    icon: FileText,
    action: () => { window.location.href = '/quotations' },
    keywords: ['navigate', 'quotes', 'quotations', 'rfq'],
    category: 'navigation',
  },
  {
    id: 'go-pricelist',
    label: 'Go to Price List',
    icon: Package,
    action: () => { window.location.href = '/pricelist' },
    keywords: ['navigate', 'price', 'list', 'catalog', 'materials'],
    category: 'navigation',
  },
  {
    id: 'go-helper',
    label: 'Open AI Helper',
    icon: Sparkles,
    action: () => { window.location.href = '/helper' },
    keywords: ['ai', 'helper', 'assistant', 'chat'],
    category: 'action',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-cfo',
    label: 'Switch to CFO Hub',
    icon: BarChart,
    action: () => { window.location.href = 'https://cfo.quotech.ai' },
    keywords: ['hub', 'cfo', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-quote',
    label: 'Switch to Quote Hub',
    icon: FileEdit,
    action: () => { window.location.href = 'https://quote.quotech.ai' },
    keywords: ['hub', 'quote', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS (for Command Palette)
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS
